interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  blockDurationMs: number;
}

interface RequestRecord {
  count: number;
  resetTime: number;
  blocked: boolean;
  blockUntil?: number;
}

class RateLimiter {
  private requests = new Map<string, RequestRecord>();
  private readonly configs: Record<string, RateLimitConfig> = {
    api: { maxRequests: 60, windowMs: 60000, blockDurationMs: 300000 }, // 60 req/min, 5min block
    conversion: { maxRequests: 100, windowMs: 60000, blockDurationMs: 60000 }, // 100 req/min, 1min block
    refresh: { maxRequests: 10, windowMs: 60000, blockDurationMs: 30000 } // 10 req/min, 30sec block
  };

  checkLimit(key: string, type: keyof typeof this.configs = 'api'): boolean {
    const config = this.configs[type];
    const now = Date.now();
    const record = this.requests.get(key) || {
      count: 0,
      resetTime: now + config.windowMs,
      blocked: false
    };

    // Check if currently blocked
    if (record.blocked && record.blockUntil && now < record.blockUntil) {
      return false;
    }

    // Reset if window expired
    if (now > record.resetTime) {
      record.count = 0;
      record.resetTime = now + config.windowMs;
      record.blocked = false;
      delete record.blockUntil;
    }

    // Check if limit exceeded
    if (record.count >= config.maxRequests) {
      record.blocked = true;
      record.blockUntil = now + config.blockDurationMs;
      this.requests.set(key, record);
      return false;
    }

    // Increment counter
    record.count++;
    this.requests.set(key, record);
    return true;
  }

  getRemainingRequests(key: string, type: keyof typeof this.configs = 'api'): number {
    const config = this.configs[type];
    const record = this.requests.get(key);
    
    if (!record) return config.maxRequests;
    
    const now = Date.now();
    if (now > record.resetTime) return config.maxRequests;
    
    return Math.max(0, config.maxRequests - record.count);
  }

  getResetTime(key: string): number {
    const record = this.requests.get(key);
    return record?.resetTime || Date.now();
  }

  isBlocked(key: string): boolean {
    const record = this.requests.get(key);
    if (!record?.blocked) return false;
    
    if (record.blockUntil && Date.now() >= record.blockUntil) {
      record.blocked = false;
      delete record.blockUntil;
      this.requests.set(key, record);
      return false;
    }
    
    return true;
  }

  cleanup(): void {
    const now = Date.now();
    for (const [key, record] of this.requests.entries()) {
      if (now > record.resetTime && (!record.blockUntil || now > record.blockUntil)) {
        this.requests.delete(key);
      }
    }
  }
}

export const rateLimiter = new RateLimiter();

// Cleanup expired records every 5 minutes
setInterval(() => {
  rateLimiter.cleanup();
}, 300000);

// Helper function to get client identifier
export function getClientId(): string {
  let clientId = localStorage.getItem('client_id');
  if (!clientId) {
    clientId = 'client_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('client_id', clientId);
  }
  return clientId;
}

// Rate limiting middleware for API calls
export function withRateLimit<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  type: keyof RateLimiter['configs'] = 'api'
): T {
  return (async (...args: Parameters<T>) => {
    const clientId = getClientId();
    
    if (!rateLimiter.checkLimit(clientId, type)) {
      const remaining = rateLimiter.getBlockTimeRemaining(clientId);
      throw new Error(`Rate limit exceeded. Try again in ${Math.ceil(remaining / 1000)} seconds.`);
    }

    return fn(...args);
  }) as T;
}